import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  useAttendanceRange,
  useAttendanceToday,
  useCompOffBalance,
  useHolidays,
  useMyLeave,
  useMyLeaveBalance,
  useOrgCalendar,
} from "@/api/queries";
import { useSession } from "@/api/session";
import { PageHeader } from "@/app/page-header";
import { NotificationsPanel } from "@/components/notifications-panel";
import { StatTile, TrendChart } from "@/components/charts";
import { LeaveBalanceMeter } from "@/components/leave-balance-meter";
import { CheckInCard } from "./check-in-card";
import { Avatar } from "@/components/ui/avatar";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { dayStatus, daysUntil, holidayMap, nextAnniversary, shiftDate, todayStr } from "@/lib/calendar";
import { orgYear } from "@/lib/org-time";
import { formatDate, formatDays } from "@/lib/format";
import { leaveLabel } from "@/lib/labels";

function recentRange() {
  const end = todayStr();
  return { start: shiftDate(end, -29), end };
}

export function MyOverviewPage() {
  const user = useSession((s) => s.user);
  const { start, end } = useMemo(recentRange, []);

  const today = useAttendanceToday();
  const range = useAttendanceRange(start, end);
  const balance = useMyLeaveBalance();
  const compOff = useCompOffBalance();
  const myLeave = useMyLeave();
  const holidays = useHolidays();
  const calendar = useOrgCalendar();

  const holidayNames = useMemo(() => holidayMap(holidays.data), [holidays.data]);

  /** Hours worked per recorded day, which is the honest personal trend. */
  const hoursTrend = useMemo(() => {
    const records = [...(range.data?.records ?? [])].sort((a, b) => a.date.localeCompare(b.date));
    return records
      .filter((record) => record.total_minutes > 0)
      .map((record) => ({
        day: formatDate(record.date, "d MMM") ?? record.date,
        hours: Math.round((record.total_minutes / 60) * 10) / 10,
      }));
  }, [range.data?.records]);

  const fullDays = (range.data?.records ?? []).filter((record) => {
    const status = dayStatus(record);
    return status === "complete";
  }).length;

  const pendingRequests = (myLeave.data ?? []).filter((r) => r.status === "pending");

  const nextHoliday = useMemo(() => {
    const todayStrValue = todayStr();
    return (holidays.data ?? [])
      .filter((h) => h.category !== "optional" && h.date >= todayStrValue)
      .sort((a, b) => a.date.localeCompare(b.date))[0];
  }, [holidays.data]);

  const upcomingCelebrations = useMemo(() => {
    const list: { id: string; name: string; daysAway: number }[] = [];
    for (const person of calendar.data?.people ?? []) {
      if (!person.date_of_birth) continue;
      const date = nextAnniversary(person.date_of_birth);
      list.push({ id: `b-${person.id}`, name: person.name ?? "Unnamed", daysAway: daysUntil(date) });
    }
    return list.filter((e) => e.daysAway <= 14).sort((a, b) => a.daysAway - b.daysAway).slice(0, 4);
  }, [calendar.data?.people]);

  const subtitle = [user?.designation, user?.team_name, user?.reporting_manager_name]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Your account"
        title={`Welcome back, ${user?.name?.split(" ")[0] ?? "there"}`}
        sub={subtitle || undefined}
      />

      <NotificationsPanel />

      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <CheckInCard />

        <div className="grid gap-4 sm:grid-cols-2">
          <StatTile
            label="Full days, last 30 days"
            value={fullDays}
            icon="check-circle"
            loading={range.isLoading}
            meta="4.5 hours or more across the day's sessions"
          />
          <StatTile
            label="Comp off available"
            value={formatDays(compOff.data?.available)}
            icon="calendar-check"
            loading={compOff.isLoading}
            meta={`${formatDays(compOff.data?.earned)} earned, ${formatDays(compOff.data?.used)} used`}
          />
          <StatTile
            label="Leave requests open"
            value={pendingRequests.length}
            icon="leave"
            loading={myLeave.isLoading}
            meta={pendingRequests.length === 0 ? "Nothing pending" : "Waiting on your manager"}
          />
          <StatTile
            label="Next company holiday"
            value={nextHoliday ? (formatDate(nextHoliday.date, "d MMM") ?? "—") : "—"}
            icon="holiday"
            loading={holidays.isLoading}
            meta={nextHoliday?.name ?? "Nothing scheduled"}
          />
        </div>
      </div>

      <TrendChart
        title="Hours you worked"
        note="Every day in the last 30 with both a check-in and a check-out recorded"
        data={hoursTrend}
        xKey="day"
        series={[{ key: "hours", label: "Hours" }]}
        area
        valueSuffix="h"
        loading={range.isLoading}
        {...(range.error ? { error: range.error.message } : {})}
        emptyBody="No completed days in the last 30. A day appears here once you have both checked in and checked out."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel className="min-w-0">
          <PanelHeader
            title="Your leave balance"
            note={`Calendar year ${balance.data?.year ?? orgYear()}`}
            actions={
              <Button asChild variant="ghost" size="sm" iconRight="arrow-right">
                <Link to="/me/leave">My leave</Link>
              </Button>
            }
          />
          <div className="p-4">
            {balance.isLoading ? (
              <SkeletonTable rows={4} cols={2} />
            ) : Object.keys(balance.data?.balance ?? {}).length === 0 ? (
              <EmptyState icon="leave" title="No entitlements on file" />
            ) : (
              <div className="flex flex-col gap-3">
                {Object.entries(balance.data?.balance ?? {}).map(([type, line]) =>
                  line ? (
                    <LeaveBalanceMeter key={type} type={type} line={line} />
                  ) : null,
                )}
              </div>
            )}
          </div>
        </Panel>

        <Panel className="min-w-0">
          <PanelHeader
            title="Coming up"
            note="Birthdays in the next 14 days"
            actions={
              <Button asChild variant="ghost" size="sm" iconRight="arrow-right">
                <Link to="/me/org-calendar">Org Calendar</Link>
              </Button>
            }
          />
          {calendar.isLoading ? (
            <SkeletonTable rows={4} cols={2} />
          ) : upcomingCelebrations.length === 0 ? (
            <EmptyState
              icon="calendar"
              title="Nothing in the next 14 days"
              body="Birthdays appear here once colleagues have entered their date of birth."
            />
          ) : (
            <ul className="divide-y divide-line">
              {upcomingCelebrations.map((event) => (
                <li key={event.id} className="flex items-center gap-3 px-4 py-3">
                  <Avatar name={event.name} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                    {event.name}
                  </span>
                  <Badge tone="accent" icon="birthday">
                    {event.daysAway === 0 ? "Today" : `in ${event.daysAway}d`}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {pendingRequests.length > 0 && (
        <Panel>
          <PanelHeader title="Your open leave requests" note="Waiting on your manager's decision" />
          <ul className="divide-y divide-line">
            {pendingRequests.map((request) => (
              <li key={request.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">
                    {leaveLabel(request.leave_type)}
                  </p>
                  <p className="truncate font-mono text-xs text-muted">
                    {formatDate(request.start_date, "d MMM")}
                    {request.start_date !== request.end_date &&
                      ` to ${formatDate(request.end_date, "d MMM")}`}
                  </p>
                </div>
                <StatusBadge status={request.status} />
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {today.data && !today.data.record && (
        <p className="text-xs text-muted">
          Attendance window shown: {formatDate(start)} to {formatDate(end)}
          {holidayNames.size > 0 && ` · ${holidayNames.size} company holidays on file`}
        </p>
      )}
    </div>
  );
}
